import { Position, TOOLTIP_HIDE_DELAY_MS_DEFAULT, TooltipKind, TooltipShowTrigger } from "@spscommerce/ds-shared";
import clsx from "clsx";
import * as React from "react";

import * as PropTypes from "../prop-types";
import { getPosition, flipPosition } from "../utils/getPosition";
import { PortalContext, usePortal } from "../utils/usePortal";

export enum TooltipVisibility {
    VISIBLE = "visible",
    DEFER_VISIBLE = "defer_visible",
    HIDDEN = "hidden",
    DELAYED_HIDDEN = "delayed_hidden",
}

export function toggleTooltipState(state: TooltipVisibility) {
    return state === TooltipVisibility.VISIBLE
        ? TooltipVisibility.HIDDEN
        : TooltipVisibility.VISIBLE;
}

const CARET_SPACING_PX = 13;

const openTips = new Map();

const propsDoc = {
    for: { type: "React.MutableRefObject<any> | string", required: true },
    hideDelay: "number",
    isShown: "TooltipVisibility",
    kind: "TooltipKind",
    offsets: "[number, number]",
    onDeferred: "() => void",
    position: "Position",
    altPosition: "Position",
    showOn: "TooltipShowTrigger",
    title: "string",
};

const propTypes = {
    ...PropTypes.spsGlobalPropTypes,
    for: PropTypes.oneOfType([PropTypes.ref<any>(), PropTypes.string]).isRequired,
    hideDelay: PropTypes.number,
    isShown: PropTypes.enumValue<TooltipVisibility>(TooltipVisibility),
    kind: PropTypes.enumValue<TooltipKind>(TooltipKind),
    offsets: PropTypes.arrayOf(PropTypes.number),
    onDeferred: PropTypes.fun<() => void>(),
    position: PropTypes.enumValue<Position>(Position),
    altPosition: PropTypes.enumValue<Position>(Position),
    showOn: PropTypes.enumValue<TooltipShowTrigger>(TooltipShowTrigger),
    title: PropTypes.string,
};

export type SpsTooltipProps = PropTypes.InferTS<typeof propTypes, HTMLDivElement>;

export function SpsTooltip({
    children,
    className,
    for: forProp,
    "data-testid": testId,
    hideDelay = TOOLTIP_HIDE_DELAY_MS_DEFAULT,
    isShown,
    kind = TooltipKind.DEFAULT,
    offsets = [0, 0],
    onDeferred,
    position = Position.TOP_MIDDLE,
    altPosition,
    showOn = TooltipShowTrigger.MOUSEOVER,
    style,
    title,
    unsafelyReplaceClassName,
}: SpsTooltipProps) {
    const iref = React.useRef({
        instanceKey: Symbol(),
        prevIsShown: isShown,
        hideTimer: null,
    });

    altPosition = altPosition || flipPosition(position);

    const [shown, setShown] = React.useState(false);

    const rootElement = React.useRef(null);
    const forElementRef = typeof forProp === "string" ? React.useRef(document.getElementById(forProp)) : forProp;

    const clearHideDelay = React.useCallback(() => {
        if (iref.current.hideTimer) {
            window.clearTimeout(iref.current.hideTimer);
            iref.current.hideTimer = null;
        }
    }, []);

    const hide = React.useCallback(() => {
        clearHideDelay();
        setShown(false);
    }, []);

    const show = React.useCallback((defer = false) => {
        // If defer is true, the tooltip will only open if no other tooltip is already active.
        // We explicitly check its value rather than truthiness because when used as an event
        // listener, the argument will be an Event object.
        if (defer === true && openTips.size > 0) {
            if (onDeferred) {
                onDeferred();
            }
            return false;
        }

        if (rootElement.current.textContent === "") {
            console.warn("SpsTooltip is empty; aborting show");
            return false;
        }

        clearHideDelay();

        for (const openTipHide of openTips.values()) {
            openTipHide();
        }

        setShown(true);
        return true;
    }, []);

    const toggle = React.useCallback((event) => {
        if (event) {
            event.stopPropagation();
        }
        // tslint:disable-next-line:no-shadowed-variable
        setShown((shown) => {
            if (shown) {
                hide();
                return false;
            } else {
                return show();
            }
        });
    }, []);

    const documentListener = React.useCallback((event) => {
        if (rootElement.current && !rootElement.current.contains(event.target)) {
            hide();
        }
    }, []);

    const delayedHide = React.useCallback(() => {
        clearHideDelay();
        iref.current.hideTimer = window.setTimeout(hide, hideDelay);
    }, []);

    React.useEffect(() => {
        if (typeof forProp === "string") {
            forElementRef.current = document.getElementById(forProp);
        }

        if (shown) {
            openTips.set(iref.current.instanceKey, hide);
        } else if (openTips.has(iref.current.instanceKey)) {
            openTips.delete(iref.current.instanceKey);
        }

        if (showOn === TooltipShowTrigger.MANUAL) {
            if (isShown !== iref.current.prevIsShown) {
                switch (isShown) {
                    case TooltipVisibility.VISIBLE:
                        show();
                        break;

                    case TooltipVisibility.DEFER_VISIBLE:
                        show(true);
                        break;

                    case TooltipVisibility.HIDDEN:
                        hide();
                        break;

                    case TooltipVisibility.DELAYED_HIDDEN:
                        delayedHide();
                        break;
                }
            }
            iref.current.prevIsShown = isShown;
        }
    }, [shown, isShown]);

    React.useEffect(() => {
        if (forElementRef.current) {
            switch (showOn) {
                case TooltipShowTrigger.MOUSEOVER:
                    forElementRef.current.addEventListener("mouseenter", show);
                    forElementRef.current.addEventListener("mouseleave", delayedHide);
                    break;

                case TooltipShowTrigger.CLICK:
                    forElementRef.current.addEventListener("click", toggle);
                    document.addEventListener("click", documentListener);
                    break;
            }
        }
    }, [forElementRef]);

    React.useEffect(() => {
        if (rootElement.current.textContent === "" && shown) {
            if (iref.current.hideTimer) {
                clearTimeout(iref.current.hideTimer);
                iref.current.hideTimer = null;
            }
            setShown(false);
        }
    }, [children, shown]);

    React.useEffect(
        () => () => {
            if (openTips.has(iref.current.instanceKey)) {
                openTips.delete(iref.current.instanceKey);
            }
            if (forElementRef.current) {
                forElementRef.current.removeEventListener("mouseenter", show);
                forElementRef.current.removeEventListener("mouseleave", delayedHide);
                forElementRef.current.removeEventListener("click", toggle);
                document.removeEventListener("click", documentListener);
            }
        },
        []
    );

    const portal = usePortal("sps-tooltip-portal");
    const portalContext = React.useContext(PortalContext);

    const [posStyle, altPositionUsed] = shown
        ? getPosition(
            rootElement,
            forElementRef,
            position,
            {
                altPosition,
                setMinWidth: false,
                offsets: [offsets[0] - CARET_SPACING_PX, offsets[1]],
                ...portalContext,
            }
        )
        : [{}, false];

    const classes = clsx(
        unsafelyReplaceClassName || "sps-tooltip",
        "z-stratum-tip",
        `sps-tooltip--${kind}`,
        `sps-tooltip--pos-${(altPositionUsed ? altPosition : position).replace(" ", "-")}`,
        shown && "sps-tooltip--shown",
        className
    );

    return portal(
        <div role="tooltip" className={classes} data-testid={testId} ref={rootElement}
            style={{ ...style, ...posStyle }}
            onMouseEnter={showOn === TooltipShowTrigger.MOUSEOVER ? clearHideDelay : null}
            onMouseLeave={showOn === TooltipShowTrigger.MOUSEOVER ? delayedHide : null}
        >
            {title && <span className="sps-tooltip__title d-block">{title}</span>}
            <span className="sps-tooltip__body d-block">{children}</span>
            <span className="sps-tooltip__caret"></span>
        </div>
    );
}

Object.assign(SpsTooltip, {
    props: propsDoc,
    propTypes,
    displayName: "SpsTooltip",
});
